import React, {ChangeEvent, useState, useEffect} from "react";
import {fetchTVShowDetails} from "../utilities/dataSources";
import moment from "moment/moment";
import {DateRangePicker} from "./DateRangePicker";
import {ProgrammeModal} from "./ProgrammeModal";
import {ShowTime} from "../TVGuide";

export function ProgrammesView({
                                   tvGuide,
                                   country,
                                   focusSearchTrigger,
                               }: {
    tvGuide: any;
    country: "south-africa" | "nigeria";
    focusSearchTrigger?: number;
}) {
    const dateKeys = Object.keys(tvGuide)
        .filter((k) => /\d{4}-\d{2}-\d{2}/.test(k))
        .filter((k) => tvGuide[k].Channels.length > 0)
        .sort();

    const minDateStr = dateKeys[0];
    const maxDateStr = dateKeys[dateKeys.length - 1];

    const minDate = new Date(minDateStr);
    const maxDate = new Date(maxDateStr + "T23:59:59");

    const [showOptions, setShowOptions] = useState(false);
    const [startDateTime, setStartDateTime] = useState<Date>(minDate);
    const [endDateTime, setEndDateTime] = useState<Date>(maxDate);
    const [isNow, setIsNow] = useState<boolean>(false);

    const handleRangeChange = (start: Date, end: Date) => {
        setStartDateTime(start);
        setEndDateTime(end);
        setIsNow(false);
    };

    const showOnNow = () => {
        if (isNow) {
            setIsNow(false);
            return;
        }
        setStartDateTime(new Date(minDate));
        setEndDateTime(new Date(maxDate));
        setIsNow(true);
    };

    const programDictionary = tvGuide["ProgramMapping"];
    const metaData = tvGuide["ProgrammeMetaData"];

    const searchInputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (focusSearchTrigger && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [focusSearchTrigger]);

    const [filter, setFilter] = useState<string>("");
    const [selectedProgramme, setSelectedProgramme] = useState<string>("");
    const [selectedProgrammeData, setSelectedProgrammeData] = useState<any>(null);
    const [selectedShowTime, setSelectedShowTime] = useState<ShowTime | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const fetchProgrammeDetails = async (showTime: ShowTime) => {
        setDetailsLoading(true);
        try {
            const response = await fetchTVShowDetails(country, showTime.uniqueEventId);
            if (response.data.Duration) {
                setSelectedProgrammeData(response.data);
                setSelectedShowTime(showTime);
            } else {
                console.warn("No duration found for this programme:", response.data);
                setSelectedProgrammeData(null);
                setSelectedShowTime(null);
                alert("Synopsis could not be fetched for this programme.");
            }
        } catch (err) {
            console.error(err);
            setSelectedProgrammeData(null);
            setSelectedShowTime(null);
            alert("Programme details could not be loaded. Please try again.");
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        if (!isNow || !selectedProgramme) return;
        const timer = setTimeout(() => {
            const el = document.querySelector('.current-time-highlight');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
        return () => clearTimeout(timer);
    }, [selectedProgramme, isNow]);

    const inRange = (programmeId: string): boolean => {
        const allShowTimes: { startTime: string; endTime: string }[] =
            metaData[programmeId]?.ShowTimes || [];

        const currentTime = new Date();

        return allShowTimes.some((st) => {
            const startDT = new Date(st.startTime);
            const endDT = new Date(st.endTime);
            let result = startDT <= endDateTime && startDT >= startDateTime;
            if (result && isNow) {
                result = currentTime >= startDT && currentTime <= endDT;
            }
            return result;
        });
    };

    const getShowTimes = (programmeId: string): ShowTime[] => {
        const out: ShowTime[] = [];
        const now = new Date();

        dateKeys.forEach((key) => {
            const channels = tvGuide[key].Channels;
            channels.forEach((ch: any) => {
                ch.Programmes.forEach((p: any) => {
                    if (p.TitleId !== programmeId) return;
                    const startDT = new Date(`${key}T${p.StartTime}`);
                    const endDT = new Date(`${key}T${p.EndTime}`);
                    if (startDT < startDateTime || startDT > endDateTime) return;
                    if (isNow && !(now >= startDT && now <= endDT)) return;
                    out.push({
                        dateYYYMMDD: key,
                        date: `${key.slice(8)}-${moment(key).format("ddd")}`,
                        startTime: p.StartTime,
                        endTime: p.EndTime,
                        channelNumber: ch.Number,
                        channelName: ch.Name,
                        uniqueEventId: p.UniqueEventID,
                    });
                });
            });
        });

        return out;
    };

    const applyFilter = (programmeId: string): boolean => {
        if (!inRange(programmeId)) return false;
        if (filter.length < 3) return true;

        const lower = programDictionary[programmeId].toLowerCase();
        return filter
            .toLowerCase()
            .split(" ")
            .every((term) => lower.includes(term));
    };

    function isCurrentTime(date: string | undefined, startTime: string, endTime: string) {
        if (!date) return false;
        const [y, mo, d] = date.split('-').map(Number);
        const [sh, sm] = startTime.split(':').map(Number);
        const [eh, em] = endTime.split(':').map(Number);

        const start = new Date(y!, mo! - 1, d!, sh!, sm!, 0, 0);
        let end = new Date(y!, mo! - 1, d!, eh!, em!, 0, 0);

        // Handle shows that span midnight (end time before start)
        if (end.getTime() <= start.getTime()) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        const now = new Date();
        return now >= start && now <= end;
    }

    return (
        <>
            <div
                className="options-toggle"
                onClick={() => setShowOptions((v) => !v)}
            >
                Additional Options {showOptions ? "▾" : "▸"}
            </div>

            {showOptions && (
                <div className="additional-options" style={{marginBottom: 18}}>
                    <DateRangePicker
                        minDate={minDate}
                        maxDate={maxDate}
                        startDate={startDateTime}
                        endDate={endDateTime}
                        onChange={handleRangeChange}
                    />
                    <div style={{display: "flex", gap: 8, marginTop: 14, justifyContent: "center"}}>
                        <button
                            className={`modal-btn ${isNow ? "modal-btn-primary" : ""}`}
                            style={{margin: 0}}
                            onClick={showOnNow}
                        >
                            {isNow ? "Showing what's on now ✓" : "What's on now"}
                        </button>
                    </div>
                </div>
            )}

            <input
                ref={searchInputRef}
                className="filterTextBox"
                placeholder="Search programmes..."
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFilter(e.target.value)
                }
            />

            <ul className="programmes-list">
                {Object.keys(programDictionary)
                    .filter(applyFilter)
                    .map((pid) => (
                        <li
                            key={pid}
                            className="programmeItem"
                            onClick={() => setSelectedProgramme(pid)}
                        >
                            <div className="programmeName">
                                <span>{programDictionary[pid]}</span>
                                <span className="duration-tag">
                                    {metaData[pid]?.Duration || "NA"}
                                </span>
                            </div>
                            {selectedProgramme === pid && (
                                <div className="showtime-list">
                                    {getShowTimes(pid).map((st, i) => {
                                        const live = isCurrentTime(st.dateYYYMMDD, st.startTime, st.endTime);
                                        return (
                                            <div
                                                className={`showTime ${live ? 'current-time-highlight' : ''}`}
                                                key={i} onClick={() => fetchProgrammeDetails(st)}>
                                                <span className="date">{st.date}</span>
                                                <span className="startTime">{st.startTime}</span>
                                                <span className="endTime">{st.endTime}</span>
                                                <span className="channelNumber">{st.channelNumber}</span>
                                                <span className="channelName">{st.channelName}</span>
                                                {live && <span className="live-badge">Live</span>}
                                                <a
                                                    className="search-link"
                                                    href={`https://google.com/search?q=${encodeURIComponent(
                                                        programDictionary[pid]
                                                    )}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    🔍
                                                </a>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </li>
                    ))}
            </ul>

            {detailsLoading && (
                <div className="modal-backdrop details-loading">
                    <div className="pc-spinner" />
                </div>
            )}

            {selectedProgrammeData && (
                <ProgrammeModal
                    data={selectedProgrammeData}
                    showTime={selectedShowTime}
                    country={country}
                    onClose={() => setSelectedProgrammeData(null)}
                />
            )}
        </>
    );
}
